import type { Document, YAMLSeq } from "yaml";
import { isScalar, isSeq, Scalar } from "yaml";
import { type Field, THOUGHTS } from "./schema.ts";

export type FieldValue = string | string[] | boolean;

/**
 * Reads one field out of a YAML document as a plain value.
 */
export function readField(doc: Document, field: Field): FieldValue {
  const node = doc.get(field.key, true);
  switch (field.type) {
    case "tags":
      return readTags(node);
    case "boolean":
      return isScalar(node) ? node.value === true : false;
    default:
      return isScalar(node) && node.value !== null && node.value !== undefined
        ? String(node.value)
        : "";
  }
}

/**
 * Writes one field into a YAML document, keeping the existing quote and list
 * style, comments and key order. A missing key is added at the end.

 */
export function writeField(
  doc: Document,
  field: Field,
  value: string | string[] | boolean,
  options: { tagsStyle?: "flow" | "block"; quoteStrings?: boolean } = {},
): void {
  const node = doc.get(field.key, true);

  if (field.type === "tags") {
    const tags = cleanTags(Array.isArray(value) ? value : String(value).split(","));
    // New tags follow the quoting already used in the list; an empty list uses the schema.
    const first = isSeq(node) ? node.items.find(isScalar) : undefined;
    const type = first ? first.type : quoted(options.quoteStrings);
    const items = tags.map((tag) => text(tag, type));
    if (isSeq(node)) {
      node.items = items;
      return;
    }
    const seq = doc.createNode([]) as YAMLSeq;
    seq.items = items;
    seq.flow = (options.tagsStyle ?? THOUGHTS.tagsStyle) === "flow";
    doc.set(field.key, seq);
    return;
  }

  if (field.type === "boolean") {
    if (isScalar(node)) node.value = Boolean(value);
    else doc.set(field.key, Boolean(value));
    return;
  }

  const next = String(value);
  if (isScalar(node)) {
    node.value = next;
    // `title:` with nothing after it has no quote style yet; give it the schema's.
    if (node.type === undefined && field.type === "text") node.type = quoted(options.quoteStrings);
  } else {
    doc.set(
      field.key,
      text(next, field.type === "text" ? quoted(options.quoteStrings) : undefined),
    );
  }
}

function quoted(flag: boolean | undefined): Scalar.Type | undefined {
  return (flag ?? THOUGHTS.quoteStrings) ? "QUOTE_DOUBLE" : undefined;
}

function text(value: string, type: Scalar.Type | undefined): Scalar {
  const scalar = new Scalar(value);
  if (type) scalar.type = type;
  return scalar;
}

/** @param {unknown} node */
function readTags(node: unknown): string[] {
  if (isSeq(node)) {
    return node.items
      .map((item) => (isScalar(item) ? String(item.value ?? "") : ""))
      .map((tag) => tag.trim())
      .filter(Boolean);
  }
  if (isScalar(node) && typeof node.value === "string") {
    return cleanTags(node.value.split(","));
  }
  return [];
}

/**
 * Trims, drops empties and removes repeats (ignoring case) while keeping order.
 * @param {string[]} tags
 */
export function cleanTags(tags: string[]): string[] {
  const seen = new Set();
  const out = [];
  for (const raw of tags) {
    const tag = String(raw).trim();
    if (!tag) continue;
    const id = tag.toLowerCase();
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(tag);
  }
  return out;
}
